import { useEffect, useMemo, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';
import { parseMoneyInput } from '@/lib/subscriptionPlans';
import { loadPays, loadVilles, villesForPays, type PaysOption, type VilleOption } from '@/lib/geo';
import {
  MISSION_CURRENCIES,
  MISSION_PLATFORMS,
  type MissionInput,
  type MissionMedia,
} from '@/lib/missions';

interface Props {
  monaie: string;
  ville: string;
  onClose: () => void;
  onSubmit: (input: MissionInput) => Promise<void>;
}

const TYPES_CONTENU = [
  { key: 'video', label: 'Vidéo', icon: 'ri-video-line' },
  { key: 'images', label: 'Images', icon: 'ri-image-line' },
  { key: 'post', label: 'Post / texte', icon: 'ri-file-text-line' },
];

/** Création d'une campagne de visibilité (mission). */
export default function CreateMissionModal({ monaie, ville: villeInitial, onClose, onSubmit }: Props) {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [plateformes, setPlateformes] = useState<string[]>(['tiktok', 'instagram']);
  const [typeContenu, setTypeContenu] = useState('video');
  const [media, setMedia] = useState<MissionMedia[]>([]);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [uploading, setUploading] = useState(false);
  const [lienContenu, setLienContenu] = useState('');
  const [tarifText, setTarifText] = useState('');
  const [devise, setDevise] = useState(MISSION_CURRENCIES.includes(monaie) ? monaie : 'MAD');
  const [vuesText, setVuesText] = useState('');
  const [pays, setPays] = useState('');
  const [ville, setVille] = useState(villeInitial || '');
  const [paysList, setPaysList] = useState<PaysOption[]>([]);
  const [villesList, setVillesList] = useState<VilleOption[]>([]);
  const [musiqueAutorisee, setMusiqueAutorisee] = useState(true);
  const [dateLimite, setDateLimite] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Référentiel géographique (pays → villes) chargé depuis la base.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [p, v] = await Promise.all([loadPays(), loadVilles()]);
        if (!active) return;
        setPaysList(p);
        setVillesList(v);
      } catch {
        /* référentiel indisponible : saisie libre conservée */
      }
    })();
    return () => { active = false; };
  }, []);

  const isInternational = pays === 'international';
  const villesDispo = useMemo(() => {
    if (!pays || isInternational) return [];
    return villesForPays(pays, paysList, villesList);
  }, [pays, isInternational, paysList, villesList]);

  const handlePays = (value: string) => {
    setPays(value);
    setVille(value === 'international' ? '' : '');
  };

  const togglePlateforme = (key: string) => {
    setPlateformes((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const addMedia = (url: string, type: string) => {
    const value = url.trim();
    if (!value) return;
    setMedia((prev) => [...prev, { url: value, type }]);
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadMediaFile(file, 'missions');
      addMedia(url, file.type.startsWith('video') ? 'video' : 'image');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec du téléversement.');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!titre.trim()) {
      setError('Donnez un titre à la mission.');
      return;
    }
    if (plateformes.length === 0) {
      setError('Choisissez au moins un réseau social.');
      return;
    }
    if (!media.length && !lienContenu.trim()) {
      setError('Ajoutez au moins un média (visuel / vidéo) ou un lien de contenu.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        titre: titre.trim(),
        description: description.trim(),
        plateformes,
        typeContenu,
        media,
        lienContenu: lienContenu.trim(),
        tarif1000Vues: tarifText.trim() ? parseMoneyInput(tarifText) : 0,
        monaie: devise,
        vuesCibles: vuesText.trim() ? Number(vuesText.replace(/\D/g, '')) : 0,
        ville: isInternational ? '' : ville.trim(),
        pays: isInternational ? 'International' : pays,
        musiqueAutorisee,
        dateLimite,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de créer la mission.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div>
            <h3 className="text-base font-bold font-heading text-foreground-950">Créer une mission</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Proposez un contenu à partager et un tarif par 1000 vues.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de la mission <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="ex. 10 créateurs pour lancer ma nouvelle collection"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description / consigne à publier</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Texte à utilise, hashtags, mentions à inclure..."
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">{description.length}/500</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Réseaux concernés</label>
            <div className="flex flex-wrap gap-2">
              {MISSION_PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => togglePlateforme(p.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    plateformes.includes(p.key)
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={p.icon}></i>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Type de contenu</label>
            <div className="flex flex-wrap items-center gap-1 p-1 bg-background-100 rounded-full w-fit">
              {TYPES_CONTENU.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTypeContenu(t.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    typeContenu === t.key ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  <i className={t.icon}></i>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Médias à publier (images / vidéos)</label>
            {media.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {media.map((m, index) => (
                  <div key={`${m.url}-${index}`} className="relative w-20 h-20 rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
                    {m.type === 'video' ? (
                      <video src={m.url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => setMedia((prev) => prev.filter((_, i) => i !== index))}
                      className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-foreground-950/70 text-background-50 text-[10px] cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
                className="sm:w-28 px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="image">Image</option>
                <option value="video">Vidéo</option>
              </select>
              <input
                type="text"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="Collez un lien https://…"
                className="flex-1 min-w-0 px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { addMedia(mediaUrl, mediaType); setMediaUrl(''); }}
                  className="px-3 py-2.5 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
                >
                  <i className="ri-add-line mr-1"></i>Ajouter
                </button>
                <label className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
                  {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                  <span>{uploading ? 'Envoi…' : 'Téléverser'}</span>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) => { handleUpload(e.target.files?.[0] ?? null); e.target.value = ''; }}
                  />
                </label>
              </div>
            </div>
            <p className="text-[11px] text-foreground-400 mt-1">Le candidat devra publier ce visuel avec la description ci-dessus.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Lien de référence (facultatif)</label>
            <input
              type="text"
              value={lienContenu}
              onChange={(e) => setLienContenu(e.target.value)}
              placeholder="https://drive.google.com/… ou lien du produit"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Montant / 1000 vues</label>
              <input
                type="text"
                inputMode="decimal"
                value={tarifText}
                onChange={(e) => setTarifText(e.target.value)}
                placeholder="ex. 20 ou 0,5"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Devise</label>
              <select
                value={devise}
                onChange={(e) => setDevise(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                {MISSION_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Vues visées</label>
              <input
                type="text"
                inputMode="numeric"
                value={vuesText}
                onChange={(e) => setVuesText(e.target.value)}
                placeholder="ex. 10000"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Pays ciblé</label>
              <select
                value={pays}
                onChange={(e) => handlePays(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Tous les pays (sans restriction)</option>
                <option value="international">🌍 International — tous les pays</option>
                {paysList.map((p) => (
                  <option key={p.id} value={p.nom_pays}>{p.nom_pays}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ville / zone ciblée</label>
              {isInternational ? (
                <div className="w-full px-3 py-2.5 bg-secondary-50 border border-secondary-200/70 rounded-lg text-sm text-secondary-900 flex items-center gap-2">
                  <i className="ri-global-line"></i>
                  Ouvert aux créateurs de tous les pays
                </div>
              ) : !pays ? (
                <input
                  type="text"
                  value={ville}
                  onChange={(e) => setVille(e.target.value)}
                  placeholder="ex. Brazzaville (vide = partout)"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              ) : villesDispo.length > 0 ? (
                <select
                  value={ville}
                  onChange={(e) => setVille(e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Toutes les villes de {pays}</option>
                  {villesDispo.map((v) => (
                    <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={ville}
                  onChange={(e) => setVille(e.target.value)}
                  placeholder={`Ville de ${pays} (facultatif)`}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Date limite (facultatif)</label>
              <input
                type="date"
                value={dateLimite}
                onChange={(e) => setDateLimite(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-3 bg-background-100 rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground-900">Autoriser la musique d&apos;un tiers</p>
              <p className="text-[11px] text-foreground-500">Le candidat peut utiliser un son / une musique dans son montage.</p>
            </div>
            <button
              type="button"
              onClick={() => setMusiqueAutorisee((v) => !v)}
              className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                musiqueAutorisee ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'
              }`}
              aria-label="Autoriser la musique"
            >
              <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-megaphone-line"></i>}
            Publier la mission
          </button>
        </div>
      </div>
    </div>
  );
}